import { useEffect, useState } from "react";
import { IoMdClose } from "react-icons/io";
import {
  Button,
  TextField,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from "@mui/material";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaCloudUploadAlt,
  FaFileImage,
  FaFilePdf,
  FaDownload,
  FaLayerGroup,
  FaChevronDown,
  FaChevronRight,
  FaStore,
  FaTruck,
} from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  getPaymentBatchDetails,
  GetPaymentBatchDetailsState,
  resetGetPaymentBatchDetails,
  selectGetPaymentBatchDetails,
} from "../slices/get-payment-batch-details.slice";
import {
  confirmBatchPayment,
  ConfirmBatchPaymentState,
  resetConfirmBatchPayment,
  selectConfirmBatchPayment,
} from "../slices/confirm-batch-payment.slice";
import {
  rejectBatchPayment,
  RejectBatchPaymentState,
  resetRejectBatchPayment,
  selectRejectBatchPayment,
} from "../slices/reject-batch-payment.slice";
import { dateSetup } from "features/stock-ordering/presentation/components/stock-ordering-utils";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import {
  GetProductDataRepository,
  GetProductDataResponse,
} from "features/stock-ordering/data/stock-ordering.repository";

interface BatchPaymentConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  batchId: number | null;
  readOnly?: boolean; // true for store users on tab 1, and all users on tab 2
  isCompleted?: boolean; // true when viewing from tab 2
}

// Helper to check if a filename is an image
const isImageFile = (filename: string | null | undefined): boolean => {
  if (!filename) return false;
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];
  const lowerFilename = filename.toLowerCase();
  return imageExtensions.some((ext) => lowerFilename.endsWith(ext));
};

const getFileUrl = (filename: string | null | undefined): string => {
  if (!filename) return "";
  return `${REACT_APP_DOMAIN_URL}api/assets/uploads/multim/payment/${filename}`;
};

const getReceiptUrl = (filename: string | null | undefined): string => {
  if (!filename) return "";
  return `${REACT_APP_DOMAIN_URL}api/assets/uploads/multim/receipts/${filename}`;
};

const formatCurrency = (amount: number | string | null) => {
  const num = typeof amount === "string" ? parseFloat(amount) : amount;
  if (num === null || isNaN(num as number)) return "₱0.00";
  return `₱${(num as number).toLocaleString("en-PH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export function BatchPaymentConfirmationModal(
  props: BatchPaymentConfirmationModalProps,
) {
  const {
    open,
    onClose,
    batchId,
    readOnly = false,
    isCompleted = false,
  } = props;

  const dispatch = useAppDispatch();
  const batchDetailsState = useAppSelector(selectGetPaymentBatchDetails);
  const confirmState = useAppSelector(selectConfirmBatchPayment);
  const rejectState = useAppSelector(selectRejectBatchPayment);

  const [officialReceipt, setOfficialReceipt] = useState<File | null>(null);
  const [remarks, setRemarks] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState("");
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  // Product data per order
  const [orderProducts, setOrderProducts] = useState<Record<string, any[]>>({});
  const [productsLoading, setProductsLoading] = useState(false);

  // Fetch batch details when modal opens
  useEffect(() => {
    if (open && batchId) {
      dispatch(getPaymentBatchDetails(batchId));
    }
    return () => {
      dispatch(resetGetPaymentBatchDetails());
    };
  }, [open, batchId]);

  // Fetch products when batch details are loaded
  useEffect(() => {
    if (
      batchDetailsState.status === GetPaymentBatchDetailsState.success &&
      batchDetailsState.data?.orders
    ) {
      fetchOrderProducts(batchDetailsState.data.orders);
    }
  }, [batchDetailsState.status]);

  const fetchOrderProducts = async (ordersList: any[]) => {
    setProductsLoading(true);
    const productsMap: Record<string, any[]> = {};

    try {
      const productPromises = ordersList.map(async (order) => {
        try {
          const response: GetProductDataResponse =
            await GetProductDataRepository({
              orderId: order.id.toString(),
            });
          const data = response.data?.data?.product_data ?? [];
          productsMap[order.id.toString()] = data;
        } catch {
          productsMap[order.id.toString()] = [];
        }
      });

      await Promise.all(productPromises);
      setOrderProducts(productsMap);
    } catch {
      // Silently fail
    } finally {
      setProductsLoading(false);
    }
  };

  // Handle successful confirmation
  useEffect(() => {
    if (confirmState.status === ConfirmBatchPaymentState.success) {
      dispatch(resetConfirmBatchPayment());
      handleClose();
    }
  }, [confirmState.status]);

  // Handle successful rejection
  useEffect(() => {
    if (rejectState.status === RejectBatchPaymentState.success) {
      dispatch(resetRejectBatchPayment());
      setShowRejectDialog(false);
      handleClose();
    }
  }, [rejectState.status]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleClose = () => {
    setOfficialReceipt(null);
    setRemarks("");
    setPreviewUrl(null);
    setShowRejectDialog(false);
    setRejectReason("");
    setExpandedOrders(new Set());
    setOrderProducts({});
    dispatch(resetConfirmBatchPayment());
    dispatch(resetRejectBatchPayment());
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Only JPG, PNG, GIF, and PDF files are allowed");
        return;
      }
      setOfficialReceipt(file);
      if (file.type.startsWith("image/")) {
        setPreviewUrl(URL.createObjectURL(file));
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleConfirm = () => {
    if (!batchId) return;
    dispatch(
      confirmBatchPayment({
        batchId: batchId,
        officialReceipt: officialReceipt,
        remarks: remarks || undefined,
      }),
    );
  };

  const handleReject = () => {
    if (!batchId || !rejectReason.trim()) return;
    dispatch(
      rejectBatchPayment({
        batchId: batchId,
        reason: rejectReason,
      }),
    );
  };

  const handleDownloadFile = (
    filename: string | null | undefined,
    isReceipt: boolean = false,
  ) => {
    if (!filename) return;
    const url = isReceipt ? getReceiptUrl(filename) : getFileUrl(filename);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewImage = (src: string) => {
    setImageModalSrc(src);
    setShowImageModal(true);
  };

  const isConfirming =
    confirmState.status === ConfirmBatchPaymentState.inProgress;
  const isRejecting = rejectState.status === RejectBatchPaymentState.inProgress;
  const isLoading =
    batchDetailsState.status === GetPaymentBatchDetailsState.inProgress;

  const batch = batchDetailsState.data?.batch;
  const orders = batchDetailsState.data?.orders || [];
  const supplierPaymentInfo = batchDetailsState.data?.supplier_payment_info;
  const paymentHistory = batchDetailsState.data?.payment_history || [];

  const paymentProofIsImage = isImageFile(batch?.payment_proof_image);
  const paymentProofUrl = getFileUrl(batch?.payment_proof_image);
  const receiptIsImage = isImageFile(batch?.official_receipt_image);
  const receiptUrl = getReceiptUrl(batch?.official_receipt_image);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30 bg-black bg-opacity-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 relative">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-2xl px-6 py-4 flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <FaLayerGroup className="text-xl" />
              <div>
                <h2 className="text-lg font-bold">
                  {isCompleted
                    ? "Batch Payment Details"
                    : "Batch Payment Verification"}
                </h2>
                {batch && (
                  <p className="text-green-100 text-sm">{batch.batch_code}</p>
                )}
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-green-200 transition-colors"
            >
              <IoMdClose className="text-2xl" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <CircularProgress size={48} sx={{ color: "#16a34a" }} />
                <p className="text-gray-500 mt-4">Loading batch details...</p>
              </div>
            ) : batchDetailsState.status ===
              GetPaymentBatchDetailsState.fail ? (
              <div className="bg-red-50 text-red-700 p-4 rounded-lg text-center">
                {batchDetailsState.message || "Failed to load batch details."}
              </div>
            ) : batch ? (
              <>
                {/* Batch Summary */}
                <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600 font-medium">
                        Total Batch Amount
                      </p>
                      <p className="text-3xl font-bold text-green-800">
                        {formatCurrency(batch.total_amount)}
                      </p>
                    </div>
                    <Chip
                      icon={<FaLayerGroup />}
                      label={`${batch.order_count} orders`}
                      sx={{
                        backgroundColor: "#dcfce7",
                        color: "#166534",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                  {batch.payment_remarks && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-green-600 uppercase font-medium">
                        Remarks from Store
                      </p>
                      <p className="text-gray-700 text-sm mt-1">
                        {batch.payment_remarks}
                      </p>
                    </div>
                  )}
                </div>

                {/* Read-only notice for store users */}
                {readOnly && !isCompleted && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                    <p className="text-blue-700 text-sm">
                      Batch payment proof has been submitted. Waiting for the
                      supplier to verify and confirm all orders in this batch.
                    </p>
                  </div>
                )}

                {/* Previously rejected notice */}
                {batch.rejected_at && !isCompleted && (
                  <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                    <p className="text-xs text-red-500 uppercase font-medium">
                      Previously Rejected
                    </p>
                    <p className="text-red-700 text-sm">
                      {batch.rejection_reason || "No reason provided"}
                    </p>
                    <p className="text-xs text-red-400 mt-1">
                      {dateSetup(batch.rejected_at, true)}
                    </p>
                  </div>
                )}

                {/* Payment Proof Section */}
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                  <h3 className="text-md font-semibold text-blue-800 mb-4 flex items-center space-x-2">
                    <FaFileImage className="text-blue-600" />
                    <span>Submitted Payment Proof</span>
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Image/File Preview */}
                    <div className="flex flex-col items-center">
                      {batch.payment_proof_image ? (
                        paymentProofIsImage ? (
                          <div
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleViewImage(paymentProofUrl)}
                          >
                            <img
                              src={paymentProofUrl}
                              alt="Payment Proof"
                              className="max-w-[250px] max-h-[250px] object-contain rounded-lg shadow-md border border-gray-200"
                            />
                            <p className="text-xs text-blue-600 mt-2 text-center">
                              Click to view full size
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <div className="bg-gray-100 rounded-lg p-6">
                              <FaFilePdf className="text-5xl text-red-500 mx-auto" />
                            </div>
                            <p className="text-sm text-gray-600 font-medium">
                              {batch.payment_proof_image}
                            </p>
                          </div>
                        )
                      ) : (
                        <div className="bg-gray-100 rounded-lg p-8 text-center">
                          <FaFileImage className="text-4xl text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">
                            No payment proof uploaded
                          </p>
                        </div>
                      )}

                      {batch.payment_proof_image && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FaDownload />}
                          onClick={() =>
                            handleDownloadFile(batch.payment_proof_image)
                          }
                          sx={{
                            mt: 2,
                            borderColor: "#2563eb",
                            color: "#2563eb",
                            "&:hover": {
                              borderColor: "#1d4ed8",
                              backgroundColor: "rgba(37, 99, 235, 0.04)",
                            },
                          }}
                        >
                          Download File
                        </Button>
                      )}
                    </div>

                    {/* Payment Details */}
                    <div className="space-y-3">
                      {batch.payment_proof_uploaded_at && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase font-medium">
                            Uploaded On
                          </p>
                          <p className="text-gray-900 font-semibold">
                            {dateSetup(batch.payment_proof_uploaded_at, true)}
                          </p>
                        </div>
                      )}

                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Batch Code
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {batch.batch_code}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-gray-500 uppercase font-medium">
                          Orders in Batch
                        </p>
                        <p className="text-gray-900 font-semibold">
                          {batch.order_count}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Official Receipt (shown in completed view) */}
                {isCompleted && batch.official_receipt_image && (
                  <div className="bg-green-50 rounded-xl p-5 border border-green-200">
                    <h3 className="text-md font-semibold text-green-800 mb-4 flex items-center space-x-2">
                      <FaCheckCircle className="text-green-600" />
                      <span>Official Receipt</span>
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex flex-col items-center">
                        {receiptIsImage ? (
                          <div
                            className="cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => handleViewImage(receiptUrl)}
                          >
                            <img
                              src={receiptUrl}
                              alt="Official Receipt"
                              className="max-w-[250px] max-h-[250px] object-contain rounded-lg shadow-md border border-gray-200"
                            />
                            <p className="text-xs text-green-600 mt-2 text-center">
                              Click to view full size
                            </p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center space-y-3">
                            <div className="bg-gray-100 rounded-lg p-6">
                              <FaFilePdf className="text-5xl text-red-500 mx-auto" />
                            </div>
                            <p className="text-sm text-gray-600 font-medium">
                              {batch.official_receipt_image}
                            </p>
                          </div>
                        )}

                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<FaDownload />}
                          onClick={() =>
                            handleDownloadFile(
                              batch.official_receipt_image,
                              true,
                            )
                          }
                          sx={{
                            mt: 2,
                            borderColor: "#16a34a",
                            color: "#16a34a",
                            "&:hover": {
                              borderColor: "#15803d",
                              backgroundColor: "rgba(22, 163, 74, 0.04)",
                            },
                          }}
                        >
                          Download Receipt
                        </Button>
                      </div>

                      <div className="space-y-3">
                        {batch.confirmed_at && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-medium">
                              Confirmed On
                            </p>
                            <p className="text-gray-900 font-semibold">
                              {dateSetup(batch.confirmed_at, true)}
                            </p>
                          </div>
                        )}
                        {batch.confirmation_remarks && (
                          <div>
                            <p className="text-xs text-gray-500 uppercase font-medium">
                              Confirmation Remarks
                            </p>
                            <p className="text-gray-700 text-sm bg-white p-2 rounded border">
                              {batch.confirmation_remarks}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Orders in Batch (Accordion with Products) */}
                <div className="border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 bg-gray-50">
                    <span className="font-semibold text-gray-800 flex items-center space-x-2">
                      <FaStore className="text-gray-600" />
                      <span>Orders in this Batch ({orders.length})</span>
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          const allIds = new Set(
                            orders.map((o: any) => Number(o.id)),
                          );
                          setExpandedOrders(allIds);
                        }}
                        className="text-xs text-green-600 hover:text-green-800 font-medium"
                      >
                        Expand All
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => setExpandedOrders(new Set())}
                        className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                      >
                        Collapse All
                      </button>
                    </div>
                  </div>

                  <div className="divide-y">
                    {orders.map((order: any) => {
                      const isExpanded = expandedOrders.has(Number(order.id));
                      const products = orderProducts[order.id.toString()] ?? [];
                      const orderTotal = products.reduce(
                        (sum: number, p: any) =>
                          sum +
                          (p.commited_qty || p.order_qty) *
                            (p.productCost || 0),
                        0,
                      );

                      return (
                        <div key={order.id}>
                          {/* Accordion Header */}
                          <button
                            onClick={() => {
                              const next = new Set(expandedOrders);
                              if (next.has(Number(order.id))) {
                                next.delete(Number(order.id));
                              } else {
                                next.add(Number(order.id));
                              }
                              setExpandedOrders(next);
                            }}
                            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="flex items-center space-x-3">
                              {isExpanded ? (
                                <FaChevronDown className="text-gray-400 text-sm flex-shrink-0" />
                              ) : (
                                <FaChevronRight className="text-gray-400 text-sm flex-shrink-0" />
                              )}
                              <div>
                                <p className="font-semibold text-gray-800">
                                  {order.po_number || `ORD-${order.id}`}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {order.store_name}
                                  {order.actual_delivery_date && (
                                    <span className="text-gray-400">
                                      {" · Delivered "}
                                      {dateSetup(
                                        order.actual_delivery_date,
                                        false,
                                      )}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <span className="font-bold text-green-700">
                                {formatCurrency(Number(order.total_cost))}
                              </span>
                              {products.length > 0 && (
                                <p className="text-xs text-gray-400">
                                  {products.length} item
                                  {products.length > 1 ? "s" : ""}
                                </p>
                              )}
                            </div>
                          </button>

                          {/* Accordion Content */}
                          <Collapse in={isExpanded}>
                            <div className="px-4 pb-4 pl-12 space-y-3">
                              {/* Order Info Grid */}
                              <div className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                <div className="flex items-center space-x-2">
                                  <FaStore className="text-gray-400 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">
                                      Store
                                    </p>
                                    <p className="font-medium">
                                      {order.store_name}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <FaTruck className="text-gray-400 flex-shrink-0" />
                                  <div>
                                    <p className="text-xs text-gray-500">
                                      Supplier
                                    </p>
                                    <p className="font-medium">
                                      {order.supplier_name}
                                    </p>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">
                                    Delivery Date
                                  </p>
                                  <p className="font-medium">
                                    {order.actual_delivery_date
                                      ? dateSetup(
                                          order.actual_delivery_date,
                                          false,
                                        )
                                      : "N/A"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">
                                    Amount
                                  </p>
                                  <p className="font-medium text-green-700">
                                    {formatCurrency(Number(order.total_cost))}
                                  </p>
                                </div>
                              </div>

                              {/* Product Table */}
                              {productsLoading ? (
                                <div className="flex justify-center py-3">
                                  <CircularProgress
                                    size={20}
                                    sx={{ color: "#16a34a" }}
                                  />
                                </div>
                              ) : products.length > 0 ? (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                          Product
                                        </th>
                                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                                          Qty
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                          Unit Price
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                          Total
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                      {products.map(
                                        (product: any, idx: number) => (
                                          <tr
                                            key={idx}
                                            className="hover:bg-gray-50"
                                          >
                                            <td className="px-3 py-2">
                                              <span className="font-medium text-gray-900">
                                                {product.product_name}
                                              </span>
                                              {product.product_id && (
                                                <span className="text-xs text-gray-400 ml-1">
                                                  ({product.product_id})
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-center text-gray-700">
                                              {product.commited_qty ||
                                                product.order_qty}
                                              {product.uom && (
                                                <span className="text-xs text-gray-400 ml-1">
                                                  {product.uom}
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-gray-700">
                                              {formatCurrency(
                                                product.productCost || 0,
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-right font-medium text-gray-900">
                                              {formatCurrency(
                                                (product.commited_qty ||
                                                  product.order_qty) *
                                                  (product.productCost || 0),
                                              )}
                                            </td>
                                          </tr>
                                        ),
                                      )}
                                    </tbody>
                                    <tfoot className="bg-gray-50">
                                      <tr>
                                        <td
                                          colSpan={3}
                                          className="px-3 py-2 text-right text-xs font-semibold text-gray-600"
                                        >
                                          Subtotal:
                                        </td>
                                        <td className="px-3 py-2 text-right font-bold text-green-700">
                                          {formatCurrency(orderTotal)}
                                        </td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">
                                  No product data available.
                                </p>
                              )}
                            </div>
                          </Collapse>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Payment History */}
                {paymentHistory.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-md font-semibold text-gray-800">
                      Payment History
                    </h3>
                    <div className="space-y-2">
                      {paymentHistory.map((item: any, index: number) => (
                        <div
                          key={index}
                          className="flex items-start space-x-3 text-sm"
                        >
                          <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 flex-shrink-0" />
                          <div>
                            <p className="text-gray-800 font-medium">
                              {item.title}
                            </p>
                            {item.description && (
                              <p className="text-gray-500 text-xs">
                                {item.description}
                              </p>
                            )}
                            <p className="text-gray-400 text-xs">
                              {dateSetup(item.datetime, true)}
                              {item.first_name &&
                                ` — ${item.first_name} ${item.last_name || ""}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supplier Actions (Confirm/Reject) - only for tab 1, sellers */}
                {!readOnly && !isCompleted && (
                  <>
                    {/* Upload Official Receipt */}
                    <div className="space-y-3">
                      <h3 className="text-md font-semibold text-gray-800 flex items-center space-x-2">
                        <FaCloudUploadAlt className="text-green-600" />
                        <span>Upload Official Receipt (OR)</span>
                        <span className="text-gray-400 text-sm font-normal">
                          (Optional)
                        </span>
                      </h3>

                      <div
                        className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors ${
                          officialReceipt
                            ? "border-green-400 bg-green-50"
                            : "border-gray-300 hover:border-green-500"
                        }`}
                      >
                        <input
                          type="file"
                          id="batchOfficialReceipt"
                          accept="image/*,.pdf"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        <label
                          htmlFor="batchOfficialReceipt"
                          className="cursor-pointer flex flex-col items-center"
                        >
                          {previewUrl ? (
                            <div className="space-y-3">
                              <img
                                src={previewUrl}
                                alt="Official Receipt preview"
                                className="max-w-[200px] max-h-[200px] object-contain rounded-lg shadow-md"
                              />
                              <p className="text-sm text-green-600 font-medium">
                                {officialReceipt?.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Click to change file
                              </p>
                            </div>
                          ) : officialReceipt ? (
                            <div className="space-y-3">
                              <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center">
                                <span className="text-3xl">📄</span>
                              </div>
                              <p className="text-sm text-green-600 font-medium">
                                {officialReceipt.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Click to change file
                              </p>
                            </div>
                          ) : (
                            <>
                              <FaCloudUploadAlt className="text-4xl text-gray-400 mb-3" />
                              <p className="text-gray-600 font-medium">
                                Drop Official Receipt here or click to upload
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                Accepted: JPG, PNG, GIF, PDF (Max 5MB)
                              </p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Confirmation Remarks */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Confirmation Remarks (Optional)
                      </label>
                      <TextField
                        multiline
                        rows={3}
                        fullWidth
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Add any notes about this batch payment confirmation..."
                        variant="outlined"
                        size="small"
                      />
                    </div>

                    {/* Error Messages */}
                    {confirmState.status === ConfirmBatchPaymentState.fail && (
                      <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
                        {confirmState.message ||
                          "Failed to confirm batch payment. Please try again."}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between pt-4 border-t">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setShowRejectDialog(true)}
                        disabled={isConfirming || isRejecting}
                        startIcon={<FaTimesCircle />}
                      >
                        Reject Batch
                      </Button>

                      <div className="flex space-x-3">
                        <Button
                          variant="outlined"
                          onClick={handleClose}
                          disabled={isConfirming || isRejecting}
                          sx={{ color: "gray", borderColor: "gray" }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleConfirm}
                          disabled={isConfirming || isRejecting}
                          sx={{
                            backgroundColor: "#16a34a",
                            "&:hover": { backgroundColor: "#15803d" },
                          }}
                          startIcon={
                            isConfirming ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <FaCheckCircle />
                            )
                          }
                        >
                          {isConfirming
                            ? "Confirming..."
                            : `Confirm All ${batch.order_count} Orders`}
                        </Button>
                      </div>
                    </div>

                    {/* Reject Dialog */}
                    <Dialog
                      open={showRejectDialog}
                      onClose={() => setShowRejectDialog(false)}
                      maxWidth="sm"
                      fullWidth
                    >
                      <DialogTitle
                        sx={{ backgroundColor: "#dc2626", color: "white" }}
                      >
                        Reject Batch Payment
                      </DialogTitle>
                      <DialogContent sx={{ mt: 2 }}>
                        <p className="text-gray-600 mb-4">
                          This will reject the payment for all{" "}
                          <strong>{batch.order_count} orders</strong> in this
                          batch. The store will be notified and will need to
                          submit a new payment proof.
                        </p>
                        <TextField
                          multiline
                          rows={4}
                          fullWidth
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Enter rejection reason..."
                          variant="outlined"
                          required
                        />
                        {rejectState.status ===
                          RejectBatchPaymentState.fail && (
                          <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mt-3">
                            {rejectState.message ||
                              "Failed to reject batch payment. Please try again."}
                          </div>
                        )}
                      </DialogContent>
                      <DialogActions sx={{ p: 2 }}>
                        <Button
                          onClick={() => setShowRejectDialog(false)}
                          disabled={isRejecting}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={handleReject}
                          disabled={!rejectReason.trim() || isRejecting}
                          startIcon={
                            isRejecting ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : null
                          }
                        >
                          {isRejecting ? "Rejecting..." : "Confirm Rejection"}
                        </Button>
                      </DialogActions>
                    </Dialog>
                  </>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Image Modal */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="lg"
        sx={{ zIndex: 50 }}
      >
        <DialogContent sx={{ p: 0 }}>
          {imageModalSrc && (
            <img
              src={imageModalSrc}
              alt="Full Size"
              className="max-w-full max-h-[80vh] object-contain"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImageModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
